import { useCallback } from "react";
import { showNotification } from "@mantine/notifications";

import { IssueType } from "@protos/issues/v1/issues";
import { updateRuleBoard } from "@shared/api";
import { OptionalAnchor } from "@shared/components";
import {
  useBasePath,
  useOrgAssertDefined,
  useUpdateRepoMutation,
} from "@shared/hooks";
import { ProductWithIgnores, Repo, RuleBoardUpdate } from "@shared/types";
import { generatePathIgnoreOptions } from "@shared/utils";

const issueTypeToProduct = (issueType: IssueType): ProductWithIgnores => {
  switch (issueType) {
    // Sast and SCA share the same ignore list for now
    case IssueType.SAST:
    case IssueType.SCA:
      return "sast";
    case IssueType.SECRETS:
      return "secrets";
    default:
      throw new Error(`Issue type ${issueType} not supported`);
  }
};

const TOAST_MESSAGE_MAP: Record<IssueType, string> = {
  [IssueType.SAST]: "Code & Supply Chain path ignores",
  [IssueType.SCA]: "Code & Supply Chain path ignores",
  [IssueType.SECRETS]: "Secrets path ignores",
  [IssueType.RESEARCH]: "",
  [IssueType.UNSPECIFIED]: "",
};

/**
 * @returns a function that will save triage ignore options and return
 *         the pathIgnored and ruleDisabled values that were successfully saved
 */
export const useSaveIgnoreOptions = (
  findingPath: string,
  findingRuleId: string,
  findingIssueType: IssueType,
  findingRepo?: Repo,
  findingRuleBoard?: string
) => {
  const org = useOrgAssertDefined();
  const basePath = useBasePath();
  const { mutateAsync: updateRepoMutation } = useUpdateRepoMutation();
  const ignoreOptions = generatePathIgnoreOptions(findingPath);

  return useCallback(
    async (disableRule: boolean, ignoreOption?: string) => {
      // for passing up to parent
      let pathIgnored: string | undefined = undefined;
      let ruleDisabled: boolean = false;

      if (ignoreOption && findingRepo && findingRepo.name) {
        const product = issueTypeToProduct(findingIssueType);
        const existingIgnores =
          findingRepo.product_ignored_files[product] ?? [];

        try {
          await updateRepoMutation({
            deploymentId: org.id,
            repoName: findingRepo.name,
            repoChanges: {
              product_ignored_files: {
                [product]: [
                  ...existingIgnores,
                  ignoreOptions[ignoreOption].pathIgnore,
                ],
              },
            },
          });
          showNotification({
            message: `Successfully added to ${TOAST_MESSAGE_MAP[findingIssueType]}`,
            color: "green",
          });
          pathIgnored = ignoreOptions[ignoreOption].pathIgnore;
        } catch (err: any) {
          showNotification({
            title: `Error adding to ${TOAST_MESSAGE_MAP[findingIssueType]}: ${err.message}`,
            message: (
              <>
                Check out the{" "}
                <OptionalAnchor
                  url={
                    findingRepo.name
                      ? `${basePath}/projects/${encodeURIComponent(
                          findingRepo.name
                        )}`
                      : undefined
                  }
                >
                  project settings
                </OptionalAnchor>{" "}
                to add to the {TOAST_MESSAGE_MAP[findingIssueType]} manually.
              </>
            ),
            color: "red",
          });
        }
      }
      if (disableRule && findingRuleBoard) {
        const change = {
          item: {
            id: findingRuleId,
            type: "rule",
          },
          policy: "disabled",
          update_type: "add",
        } as RuleBoardUpdate;

        try {
          await updateRuleBoard(findingRuleBoard, [change], org.id);
          ruleDisabled = true;
          showNotification({
            title: "Success",
            message: "Successfully disabled rule",
            color: "green",
          });
        } catch (error) {
          const errorMessage = (
            <>
              Error disabling rule. Check out{" "}
              <a
                href={`${basePath}/policies`}
                target="_blank"
                rel="noopener noreferrer"
              >
                your policies.
              </a>{" "}
              to remove it manually.
            </>
          );
          showNotification({
            title: "Error",
            message: errorMessage,
            color: "red",
          });
        }
      }

      return { pathIgnored, ruleDisabled };
    },
    [
      basePath,
      findingRuleBoard,
      findingRepo,
      findingRuleId,
      ignoreOptions,
      org,
      updateRepoMutation,
      findingIssueType,
    ]
  );
};
